/**
 * LA RELECTURE D'UN TRACÉ ATTENDU — chantier « Élève », lot 8, palier L0
 * (« voir avant de produire », AB#943 ; `docs/pilotage/atelier-figures/
 * CONCEPTION.md`, § 5.1, maquette `cible.html`).
 *
 * « En relecture, on lit seulement “un tracé : N objets attendus, M
 * contraintes”. » Ici, le relecteur VOIT :
 *
 *  - `AttenduDuTrace` (colonne « Ce que Silex attend ») : la solution du prof
 *    sur la figure de l'énoncé, rejouable pas à pas, et jugée par le
 *    vérificateur ; les contraintes EN PHRASES ; chaque tracé fautif prévu,
 *    dessiné sous la solution, avec l'erreur qu'il doit déclencher — et s'il
 *    la déclenche vraiment, rejoué par le jugement du serveur. Un tracé fautif
 *    qui ne la déclenche pas est signalé ;
 *  - `BacASableDuTrace` (colonne « Ce que l'élève reçoit ») : le relecteur
 *    trace comme un élève, avec les outils de l'exercice, et lit le verdict et
 *    les constats que l'élève recevrait.
 *
 * ─── Aucun modèle, aucune écriture (I-11) ───────────────────────────────────
 *
 * Tout est calculé dans la page par les fonctions pures du paquet partagé
 * (`jugerTrace`, `eprouverTracesFautifs`, `phrasesDesContraintes`) — les
 * MÊMES que le serveur. Le bac à sable ne garde rien et n'envoie rien.
 */
import {
  FAMILLES_TRACEES_V2,
  TITRES_VERDICT_EXERCICE,
  direEtatTraceFautif,
  phrasesDesContraintes,
  resoudreConstruction,
  toleranceDe,
  formaterNombre,
  type FamilleFonctionV2,
  type JugementDeTrace,
  type OutilTrace,
} from '@silex/shared';
import { useMemo, useState, type ReactNode } from 'react';

import {
  BAC_VIDE,
  NOM_OUTIL,
  OUTILS_AU_CLIC,
  annulerDansLeBac,
  documentDuTrace,
  echelleDuTrace,
  etapesDeLaSolution,
  fermerLePolygone,
  frontieresPossibles,
  jugerLaSolution,
  jugerLeBac,
  lireParametres,
  nomDansLeBac,
  poserDansLeBac,
  svgDuTrace,
  toucherLeBac,
  tracesFautifsRejoues,
  versFigure,
  type BacASable,
  type OutilAuClic,
  type TraceRelu,
} from '../../lib/relecture-trace';
import { ToileDeTrace } from '../FigureV2Web';
import { TexteMaths } from '../TexteMaths';

const nb = (v: number): string => formaterNombre(v).replace(/^-/u, '−');

/** Le verdict d'un jugement, comme l'élève le lit. */
function Verdict({
  j,
  titreErreur,
  testid,
}: {
  readonly j: JugementDeTrace;
  readonly titreErreur: (code: string) => string;
  readonly testid: string;
}): ReactNode {
  if (j.genre === 'incomplet') {
    return (
      <div className="tr-verdict" data-testid={testid} data-verdict="incomplet">
        <b>Pas un essai.</b> L’élève lit : « {j.retroaction} » Rien n’est compté.
      </div>
    );
  }
  return (
    <div className="tr-verdict" data-testid={testid} data-verdict={j.verdict}>
      <div>
        <b>{TITRES_VERDICT_EXERCICE[j.verdict]}</b>
        {j.retroaction !== TITRES_VERDICT_EXERCICE[j.verdict] ? ` ${j.retroaction}` : null}
      </div>
      {j.erreurCode !== null ? (
        <div className="cell-sub">
          Erreur type nommée : <TexteMaths texte={titreErreur(j.erreurCode)} />
          {j.fautive !== null ? ' (tracé fautif prévu reconnu)' : ' (contrainte manquée)'}
        </div>
      ) : null}
      {j.fautive !== null && j.fautive.cheminement !== '' ? (
        <div className="cell-sub">
          <TexteMaths texte={j.fautive.cheminement} />
        </div>
      ) : null}
      <ul className="figure-v2__constats">
        {j.verification.constats.map((c) => (
          <li key={c.contrainte} className={c.etat === 'tenue' ? undefined : 'li-sub--attention'}>
            {c.texte}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────── la solution, pas à pas ─────────────────────────── */

function SolutionPasAPas({ t }: { readonly t: TraceRelu }): ReactNode {
  const etapes = useMemo(() => etapesDeLaSolution(t), [t]);
  const [k, setK] = useState(etapes.length - 1);
  const etape = etapes[Math.min(k, etapes.length - 1)];
  const svg = useMemo(
    () => (etape ? svgDuTrace(t, etape.objets, null, 'La solution du prof') : ''),
    [t, etape],
  );
  const juge = useMemo(() => jugerLaSolution(t), [t]);
  const juste = juge.genre === 'juge' && juge.verdict === 'juste';
  if (etape === undefined) return null;
  return (
    <section className="tr-bloc" data-testid="tr-solution" aria-label="La solution, pas à pas">
      <h3 className="rx-h3">La solution du prof, pas à pas</h3>
      <ToileDeTrace svg={svg} testid="tr-solution-toile" />
      <div className="tr-pas">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(0)}
          disabled={k === 0}
        >
          Début
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(Math.max(0, k - 1))}
          disabled={k === 0}
        >
          ← Étape
        </button>
        <span className="tr-pas__compte" data-testid="tr-solution-etape">
          Étape {k + 1} / {etapes.length}
        </span>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(Math.min(etapes.length - 1, k + 1))}
          disabled={k === etapes.length - 1}
        >
          Étape →
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setK(etapes.length - 1)}
          disabled={k === etapes.length - 1}
        >
          Tout
        </button>
      </div>
      <p className="cell-sub">{etape.phrase}</p>
      <p className={juste ? 'note-line' : 'note-line tr-alerte'} data-testid="tr-solution-jugee">
        {juste
          ? 'Rejouée par le vérificateur : la solution est jugée juste.'
          : `Rejouée par le vérificateur, la solution n’est PAS jugée juste : ${
              juge.genre === 'juge' ? TITRES_VERDICT_EXERCICE[juge.verdict] : juge.retroaction
            } À corriger dans la source.`}
      </p>
    </section>
  );
}

/* ─────────────────────────── les contraintes ─────────────────────────── */

function ContraintesEnPhrases({ t }: { readonly t: TraceRelu }): ReactNode {
  const phrases = useMemo(
    () => phrasesDesContraintes(t.corrige.contraintes, t.corrige.cibles),
    [t],
  );
  const tol = useMemo(() => {
    const r = resoudreConstruction(documentDuTrace(t, []), t.enonce).repere;
    return toleranceDe(r, undefined);
  }, [t]);
  return (
    <section className="tr-bloc" aria-label="Ce que le vérificateur exige">
      <h3 className="rx-h3">Ce que le vérificateur exige</h3>
      <ul className="rx-liste" data-testid="tr-contraintes">
        {phrases.map((p) => {
          const erreurs = p.contraintes
            .map((i) => t.corrige.contraintes[i]?.erreur)
            .filter((e): e is string => e !== undefined);
          return (
            <li key={p.contraintes.join('-')}>
              {p.texte}
              {erreurs.length > 0 ? (
                <div className="cell-sub">
                  Si elle manque :{' '}
                  {erreurs.map((e, i) => (
                    <span key={e}>
                      {i > 0 ? ' ; ' : ''}« <TexteMaths texte={t.titreErreur(e)} /> »
                    </span>
                  ))}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <p className="note-line">
        Tolérance : {nb(tol.distance)} unité de figure (un centième de la diagonale du repère),{' '}
        {nb(tol.angleDeg)}° sur un angle, un écart relatif de {nb(tol.relative)} sur une longueur ou
        une aire.
      </p>
    </section>
  );
}

/* ─────────────────────────── les tracés fautifs ─────────────────────────── */

function TracesFautifs({ t }: { readonly t: TraceRelu }): ReactNode {
  const epreuves = useMemo(() => tracesFautifsRejoues(t), [t]);
  const svgs = useMemo(
    () =>
      epreuves.map((e, i) =>
        e.objets === null
          ? null
          : svgDuTrace(t, e.objets, t.corrige.solution as never, `Tracé fautif ${i + 1}`),
      ),
    [t, epreuves],
  );
  if (epreuves.length === 0) {
    return (
      <section className="tr-bloc">
        <h3 className="rx-h3">Tracés fautifs prévus</h3>
        <p className="note-line" data-testid="tr-fautifs">
          Aucun tracé fautif prévu : une erreur ne se nomme que par la première contrainte manquée.
        </p>
      </section>
    );
  }
  const enDefaut = epreuves.filter((e) => e.etat !== 'declenche').length;
  return (
    <section className="tr-bloc" aria-label="Tracés fautifs prévus">
      <h3 className="rx-h3">Tracés fautifs prévus</h3>
      <p
        className={enDefaut > 0 ? 'note-line tr-alerte' : 'note-line'}
        data-testid="tr-fautifs-bilan"
      >
        {epreuves.length} tracé{epreuves.length > 1 ? 's' : ''} fautif
        {epreuves.length > 1 ? 's' : ''} rejoué{epreuves.length > 1 ? 's' : ''} par le vérificateur
        :{' '}
        {enDefaut === 0
          ? 'chacun déclenche son erreur.'
          : `${enDefaut} ne déclenche${enDefaut > 1 ? 'nt' : ''} pas son erreur.`}
      </p>
      <ol className="tr-fautifs" data-testid="tr-fautifs">
        {epreuves.map((e, i) => {
          const svg = svgs[i];
          const ok = e.etat === 'declenche';
          return (
            <li
              key={e.reponse}
              className={ok ? 'tr-fautif' : 'tr-fautif tr-fautif--defaut'}
              data-etat={e.etat}
            >
              <div className="tr-fautif__tete">
                <span className="contenus-mono">
                  <TexteMaths texte={e.reponse} />
                </span>{' '}
                →{' '}
                <b>
                  {e.erreurVisee === null ? (
                    'aucune erreur nommée'
                  ) : (
                    <TexteMaths texte={t.titreErreur(e.erreurVisee)} />
                  )}
                </b>
              </div>
              {svg ? <ToileDeTrace svg={svg} /> : null}
              <ul className="figure-v2__constats">
                {phrasesDesContraintes(e.contraintes, t.corrige.cibles).map((p) => (
                  <li key={p.contraintes.join('-')}>{p.texte}</li>
                ))}
              </ul>
              <span className={ok ? 'badge badge--acquis' : 'badge badge--a-revoir'}>
                {ok ? 'Déclenche son erreur' : 'Ne déclenche pas son erreur'}
              </span>{' '}
              <span className="cell-sub">
                <TexteMaths texte={direEtatTraceFautif(e, t.titreErreur)} />
              </span>
            </li>
          );
        })}
      </ol>
      <p className="note-line">
        Plein : le tracé fautif, construit à partir de ses contraintes. Pointillé : la solution du
        prof.
      </p>
    </section>
  );
}

/** Colonne « Ce que Silex attend » d'un exercice à tracer. */
export function AttenduDuTrace({ t }: { readonly t: TraceRelu }): ReactNode {
  return (
    <div className="tr-attendu" data-testid="tr-attendu">
      <SolutionPasAPas t={t} />
      <ContraintesEnPhrases t={t} />
      <TracesFautifs t={t} />
    </div>
  );
}

/* ─────────────────────────── le bac à sable ─────────────────────────── */

const RAYON_AIMANT_PX = 14;

export function FormulaireCourbe({ onPoser }: { readonly onPoser: (def: never) => void }): ReactNode {
  const [famille, setFamille] = useState<FamilleFonctionV2>('lineaire');
  const [parametres, setParametres] = useState('a = 1 ; b = 0');
  return (
    <div className="tr-formulaire">
      <label>
        Famille{' '}
        <select value={famille} onChange={(e) => setFamille(e.target.value as FamilleFonctionV2)}>
          {FAMILLES_TRACEES_V2.map((f) => (
            <option key={f} value={f}>
              {f.replace('_', ' ')}
            </option>
          ))}
        </select>
      </label>
      <label>
        Paramètres{' '}
        <input value={parametres} onChange={(e) => setParametres(e.target.value)} size={22} />
      </label>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() =>
          onPoser({ type: 'fonction', famille, parametres: lireParametres(parametres) } as never)
        }
      >
        Poser la courbe
      </button>
    </div>
  );
}

export function FormulaireDemiPlan({
  bac,
  onPoser,
}: {
  readonly bac: BacASable;
  readonly onPoser: (def: never) => void;
}): ReactNode {
  const possibles = frontieresPossibles(bac);
  const [frontiere, setFrontiere] = useState('');
  const [cote, setCote] = useState('dessus');
  const [stricte, setStricte] = useState(false);
  const choisie = possibles.find((o) => o.id === frontiere) ?? possibles[possibles.length - 1];
  if (choisie === undefined) {
    return (
      <p className="cell-sub">Demi-plan : trace d’abord sa frontière (une droite ou une courbe).</p>
    );
  }
  return (
    <div className="tr-formulaire">
      <label>
        Frontière{' '}
        <select value={choisie.id} onChange={(e) => setFrontiere(e.target.value)}>
          {possibles.map((o) => (
            <option key={o.id} value={o.id}>
              {nomDansLeBac(o)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Côté{' '}
        <select value={cote} onChange={(e) => setCote(e.target.value)}>
          <option value="dessus">au-dessus</option>
          <option value="dessous">au-dessous</option>
          <option value="gauche">à gauche</option>
          <option value="droite">à droite</option>
        </select>
      </label>
      <label>
        <input type="checkbox" checked={stricte} onChange={(e) => setStricte(e.target.checked)} />{' '}
        frontière exclue
      </label>
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() =>
          onPoser({ type: 'demi_plan', frontiere: choisie.id, cote, stricte } as never)
        }
      >
        Hachurer
      </button>
    </div>
  );
}

/** Colonne « Ce que l'élève reçoit » : le relecteur trace comme un élève. */
export function BacASableDuTrace({ t }: { readonly t: TraceRelu }): ReactNode {
  const outils = t.outils;
  const auClic = outils.filter((o): o is OutilAuClic =>
    (OUTILS_AU_CLIC as readonly string[]).includes(o),
  );
  const [outil, setOutil] = useState<OutilTrace>(outils[0] ?? 'point');
  const [bac, setBac] = useState<BacASable>(BAC_VIDE);
  const [jugement, setJugement] = useState<JugementDeTrace | null>(null);
  const echelle = useMemo(() => echelleDuTrace(t), [t]);
  const svg = useMemo(
    () =>
      svgDuTrace(
        t,
        bac.objets,
        jugement === null ? null : (t.corrige.solution as never),
        'Le tracé du relecteur',
      ),
    [t, bac, jugement],
  );
  const changer = (b: BacASable): void => {
    setBac(b);
    setJugement(null);
  };
  const enCours = bac.enCours
    .map((id) => bac.objets.find((o) => o.id === id))
    .filter((o) => o !== undefined)
    .map((o) => nomDansLeBac(o));
  return (
    <section className="tr-bac" data-testid="tr-bac" aria-label="Bac à sable">
      <h3 className="rx-h3">Bac à sable : tracer comme l’élève</h3>
      <p className="cell-sub">
        Les outils de l’exercice, le même aimant, le même vérificateur que le serveur. Rien n’est
        enregistré, aucun modèle n’est appelé.
      </p>
      <div className="tr-outils" role="toolbar" aria-label="Outils">
        {outils.map((o) => (
          <button
            key={o}
            type="button"
            className={o === outil ? 'btn btn--sm' : 'btn btn--ghost btn--sm'}
            aria-pressed={o === outil}
            onClick={() => {
              setOutil(o);
              setBac({ ...bac, enCours: [] });
            }}
          >
            {NOM_OUTIL[o]}
          </button>
        ))}
        <span className="tr-outils__sep" />
        <button
          type="button"
          className="btn btn--quiet btn--sm"
          onClick={() => changer(annulerDansLeBac(bac))}
        >
          Annuler
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={() => changer(BAC_VIDE)}>
          Effacer
        </button>
      </div>
      {outil === 'fonction' ? (
        <FormulaireCourbe onPoser={(def) => changer(poserDansLeBac(bac, 'f', def))} />
      ) : null}
      {outil === 'demi_plan' ? (
        <FormulaireDemiPlan bac={bac} onPoser={(def) => changer(poserDansLeBac(bac, 'h', def))} />
      ) : null}
      <ToileDeTrace
        svg={svg}
        testid="tr-bac-toile"
        onToucher={
          (auClic as readonly string[]).includes(outil)
            ? (aff, pixelsParUnite) =>
                changer(
                  toucherLeBac(
                    t,
                    bac,
                    outil as OutilAuClic,
                    versFigure(echelle, aff),
                    RAYON_AIMANT_PX / pixelsParUnite / echelle.kx,
                  ),
                )
            : undefined
        }
      />
      <div className="tr-pas">
        <span className="cell-sub" data-testid="tr-bac-etat">
          {bac.objets.length} objet{bac.objets.length > 1 ? 's' : ''} tracé
          {bac.objets.length > 1 ? 's' : ''}
          {enCours.length > 0 ? ` · ${NOM_OUTIL[outil]} en cours : ${enCours.join(', ')}` : ''}
        </span>
        {outil === 'polygone' && bac.enCours.length >= 3 ? (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => changer(fermerLePolygone(bac))}
          >
            Fermer le polygone
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn--sm"
          data-testid="tr-bac-verifier"
          onClick={() => setJugement(jugerLeBac(t, bac))}
        >
          Vérifier comme l’élève
        </button>
      </div>
      {jugement !== null ? (
        <>
          <Verdict j={jugement} titreErreur={t.titreErreur} testid="tr-bac-verdict" />
          {jugement.genre === 'juge' ? (
            <p className="note-line">
              Plein : le tracé du relecteur. Pointillé : la solution du prof.
            </p>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
